import { RootAction, DataActions, FeedActions } from '../actions';
import dayjs from 'dayjs';
import { FeedQuote, FeedSettlement, Loadable } from '@tradingblock/types';

import { QuoteSymbolQuotes, QuotesState } from '../state';
import { InitialState } from '../initialState';
import { getType } from 'typesafe-actions';

import { getLastOrClosePrice } from '../utilities';

import {
  SymbolQuoteAndChangeData,
  SymbolChangeDirection,
  ToUtcMidnight,
  getBestPrice,
  getLocalDateFromTimezone,
} from '@tradingblock/api';
import _ from 'lodash';
import { changeCalculation } from '../../../utilities/position';
import { isPremarket } from '../../../utilities/date';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const numOrZero = <T>(...values: (T | undefined)[]) => {
  const firstNonNil = _.find(values, v => !_.isNil(v) && !_.isNaN(v));
  return _.isNil(firstNonNil) ? 0 : firstNonNil;
};

const SETTLEMENT_RESET_TIME = dayjs()
  .startOf('day')
  .add(1, 'day')
  .add(2, 'hours')
  .add(5, 'minutes')
  .valueOf();

export const quoteReducer = (state = InitialState.quotes, action: RootAction): QuotesState => {
  switch (action.type) {
    case getType(DataActions.requestQuoteExtended): {
      const { symbol } = action.payload;
      return {
        ...state,
        quoteExtended: {
          ...state.quoteExtended,
          isFetching: true,
        },
      };
    }
    case getType(DataActions.receiveQuoteExtended): {
      const { symbol, quote } = action.payload;
      return {
        ...state,
        quoteExtended: {
          ...state.quoteExtended,
          isFetching: false,
          data: {
            ...state.quoteExtended.data,
            [symbol]: quote,
          },
        },
      };
    }
    case getType(DataActions.errorQuoteExtended): {
      const { symbol } = action.payload;
      return {
        ...state,
        quoteExtended: {
          ...state.quoteExtended,
          isFetching: false,
        },
      };
    }
    case getType(DataActions.requestEarnings): {
      const { symbol, period } = action.payload;
      const periodVal = period && period === 'annual' ? 'annual' : 'quarter';
      const existingSymbolVal = state.earnings[symbol] || {};
      const existing = existingSymbolVal[periodVal] || { earnings: [], isFetching: false };
      return {
        ...state,
        earnings: {
          ...state.earnings,
          [symbol]: {
            ...existingSymbolVal,
            [periodVal]: {
              ...existing,
              isFetching: true,
            },
          },
        },
      };
    }
    case getType(DataActions.receiveEarnings): {
      const { symbol, earnings, period } = action.payload;
      const periodVal = period && period === 'annual' ? 'annual' : 'quarter';
      const existingSymbolVal = state.earnings[symbol] || {};
      const existing = existingSymbolVal[periodVal] || { earnings: [], isFetching: false };
      return {
        ...state,
        earnings: {
          ...state.earnings,
          [symbol]: {
            ...existingSymbolVal,
            [periodVal]: {
              ...existing,
              isFetching: false,
              earnings: earnings || [],
            },
          },
        },
      };
    }
    case getType(DataActions.errorEarnings): {
      const { symbol, period } = action.payload;
      const periodVal = period && period === 'annual' ? 'annual' : 'quarter';
      const existingSymbolVal = state.earnings[symbol] || {};
      const existing = existingSymbolVal[periodVal] || { earnings: [], isFetching: false };
      return {
        ...state,
        earnings: {
          ...state.earnings,
          [symbol]: {
            ...existingSymbolVal,
            [periodVal]: {
              ...existing,
              isFetching: false,
            },
          },
        },
      };
    }
    case getType(DataActions.requestDividends): {
      const { symbol, range } = action.payload;
      const existing = state.dividends[symbol] || {
        next: { isFetching: false, dividends: [] },
        ['1y']: { isFetching: false, dividends: [] },
        ['2y']: { isFetching: false, dividends: [] },
        ['5y']: { isFetching: false, dividends: [] },
      };
      const existingRangeState = existing[range] || { dividends: [], isFetching: false };
      return {
        ...state,
        dividends: {
          ...state.dividends,
          [symbol]: {
            ...existing,
            [range]: {
              ...existingRangeState,
              isFetching: true,
            },
          },
        },
      };
    }
    case getType(DataActions.receiveDividends): {
      const { symbol, dividends, range } = action.payload;
      const existing = state.dividends[symbol] || {
        next: { isFetching: false, dividends: [] },
        ['1y']: { isFetching: false, dividends: [] },
        ['2y']: { isFetching: false, dividends: [] },
        ['5y']: { isFetching: false, dividends: [] },
      };
      const existingRangeState = existing[range] || { dividends: [], isFetching: false };
      return {
        ...state,
        dividends: {
          ...state.dividends,
          [symbol]: {
            ...existing,
            [range]: {
              ...existingRangeState,
              isFetching: false,
              dividends: dividends || [],
            },
          },
        },
      };
    }
    case getType(DataActions.errorDividends): {
      const { symbol, range } = action.payload;
      const existing = state.dividends[symbol] || {
        next: { isFetching: false, dividends: [] },
        ['1y']: { isFetching: false, dividends: [] },
        ['2y']: { isFetching: false, dividends: [] },
        ['5y']: { isFetching: false, dividends: [] },
      };
      const existingRangeState = existing[range] || { dividends: [], isFetching: false };
      return {
        ...state,
        dividends: {
          ...state.dividends,
          [symbol]: {
            ...existing,
            [range]: {
              ...existingRangeState,
              isFetching: false,
            },
          },
        },
      };
    }
    case getType(DataActions.fetchingQuotes): {
      const updatedQuoteData = _([action.payload.symbol])
        .flatMap()
        .reduce((acc, sym) => {
          const existingSym = acc[sym] || {};
          return {
            ...acc,
            [sym]: {
              ...existingSym,
              Symbol: sym,
              isFetching: true,
            },
          };
        }, state.quoteMetadata.data);

      return {
        ...state,
        quoteMetadata: {
          ...state.quoteMetadata,
          data: { ...updatedQuoteData },

          isFetching: true,
        },
      };
    }
    case getType(DataActions.receiveOpenPrice): {
      const { symbol, openPrice } = action.payload;
      const openToStore = isPremarket() ? 0 : openPrice;
      const updatedQuoteData = _([symbol])
        .flatMap()
        .reduce((acc, sym) => {
          const existingSym = acc[sym] || {};
          return {
            ...acc,
            [sym]: {
              ...existingSym,
              Symbol: sym,
              Open: openToStore,
            },
          };
        }, state.quoteMetadata.data);
      return {
        ...state,
        quoteMetadata: {
          ...state.quoteMetadata,
          data: { ...updatedQuoteData },
        },
      };
    }
    case getType(DataActions.receiveQuote): {
      const symbolDataAsObject = _.reduce(
        action.payload,
        (acc, v) => {
          const previous = acc[v.symbol];
          const existing = previous || v.quote;
          if (v.quote.IsSettlementSet && dayjs().isBefore(SETTLEMENT_RESET_TIME, 'hour')) {
            v.quote.LastTradePrice = v.quote.ClosePrice;
            existing.LastTradePrice = v.quote.ClosePrice;
          }

          const previousMeta = previous && previous.meta ? previous.meta : {};
          const AskPrice = numOrZero(v.quote.AskPrice, existing.AskPrice);
          const BidPrice = numOrZero(v.quote.BidPrice, existing.BidPrice);
          const LastOrClosePrice = getLastOrClosePrice(v.quote);

          const BestPriceCalculated = getBestPrice({
            bid: BidPrice,
            ask: AskPrice,
            last: LastOrClosePrice,
            defaultValue: 'avgBidAsk',
            isSettlementSet: v.quote.IsSettlementSet,
          });
          // const ReceivedTime = v.quote.QuoteTime;
          // format received time to match the format of the other time stamps, YYYY-MM-DD HH:mm:ssZ, then remove UTC offset and replace with Z
          const ReceivedTime = dayjs(v.quote.QuoteTime)
            .format('MM/DD/YYYY HH:mm:ssZ')
            .replace(/[-+]\d\d:\d\d$/, 'Z');

          const previousClosePrice = previous ? previous.ClosePrice : 0;
          const prevLastUpdate =
            previous && !_.isNil(previousMeta.closePriceLastUpdatedUnix)
              ? previousMeta.closePriceLastUpdatedUnix
              : undefined;
          const previousOutOfDate =
            prevLastUpdate && ToUtcMidnight(prevLastUpdate).unix() < ToUtcMidnight(new Date()).unix();
          const updateClosePrice =
            previousOutOfDate ||
            _.isNil(previous) ||
            previousClosePrice === 0 ||
            _.isNil(prevLastUpdate) ||
            v.quote.ClosePrice !== 0;
          const closePrice = v.quote.ClosePrice;
          const closePriceLastUpdate = updateClosePrice ? ToUtcMidnight(new Date()).unix() : prevLastUpdate;
          const { change, changePercent } = changeCalculation(
            v.quote.LastTradePrice,
            v.quote.ClosePrice,
            v.quote.ClosePriceTMinus2,
            v.quote.BidPrice,
            v.quote.AskPrice,
            v.quote.Symbol,
            v.quote.IsSettlementSet
          );

          // REST snapshots are not feed ticks: never carry forward LastTradeVolume or chart realtime will
          // re-apply the same delta on every poll when ReceivedTime/price change (runaway volume).
          const apiVol = v.quote.Volume;
          const prevBaseline = previousMeta.lastFeedCumulativeVolume;
          let lastFeedCumulativeVolume: number | undefined = prevBaseline;
          if (apiVol != null && Number.isFinite(apiVol)) {
            if (prevBaseline == null || !Number.isFinite(prevBaseline)) {
              lastFeedCumulativeVolume = apiVol;
            } else if (apiVol >= prevBaseline) {
              lastFeedCumulativeVolume = apiVol;
            } else if (prevBaseline > 0 && apiVol < prevBaseline * 0.05) {
              // Likely new session (cumulative reset); avoid treating a stale snapshot as reset when drop is small.
              lastFeedCumulativeVolume = apiVol;
            }
            // else: REST volume behind live feed — keep prior baseline for accurate next websocket delta
          }

          const quoteOpen = (v.quote as { Open?: number }).Open;
          const existingOpen = (existing as { Open?: number }).Open;
          const openFromApi = isPremarket() ? 0 : numOrZero(quoteOpen, existingOpen);

          return {
            ...acc,
            [v.symbol]: {
              ...existing,
              ...v.quote,
              AskPrice,
              BidPrice,
              BestPriceCalculated,
              LastOrClosePrice,
              LastTradePrice: v.quote.LastTradePrice,
              NetChange: v.quote.NetChange === 0 ? 0 : v.quote.NetChange,
              CalculatedNetChange: change,
              CalculatedChangePercentage: changePercent * 100,
              isFetching: false,
              isLoaded: true,
              isLoadedFromApi: true,
              ReceivedTime,
              ClosePrice: closePrice,
              Open: openFromApi,
              LastTradeVolume: 0,
              meta: {
                ...previousMeta,
                closePriceLastUpdatedUnix: closePriceLastUpdate,
                lastFeedCumulativeVolume,
              },
            },
          };
        },
        state.quoteMetadata.data
      );
      return {
        ...state,
        quoteMetadata: {
          ...state.quoteMetadata,
          isFetching: false,
          data: {
            ...symbolDataAsObject,
          },
        },
      };
    }
    case getType(FeedActions.updateQuotes): {
      const { quotes } = action.payload;
      const nextData: QuoteSymbolQuotes = { ...state.quoteMetadata.data };
      let latestReceivedTime: number | undefined = state.quoteMetadata.latestReceivedTime;

      for (const k in quotes) {
        const v = quotes[k] as FeedQuote;
        const existingValue: Loadable<SymbolQuoteAndChangeData> | undefined = nextData[k];
        const existing = existingValue || { Symbol: v.Symbol };

        if ((v as any).IsSettlementSet !== undefined && (v as any).IsSettlementSet === true) {
          const settlement: FeedSettlement = v as any;
          (existing as any).IsSettlementSet = settlement.IsSettlementSet;
          (existing as any).ClosePrice = settlement.ClosePrice;
          (existing as any).ClosePriceTMinus2 = settlement.ClosePriceTMinus2;
          (existing as any).CloseDate = settlement.CloseDate;
        }

        // Populate close prices from websocket quote data (cp/cp2 fields) when available.
        // Acts as fallback when GetQuotes API call fails and no prior data exists.
        const vAny = v as any;
        if (vAny.ClosePrice != null && vAny.ClosePrice > 0) {
          const existingClose = (existing as any).ClosePrice;
          if (existingClose == null || existingClose === 0) {
            (existing as any).ClosePrice = vAny.ClosePrice;
          }
        }
        if (vAny.ClosePriceTMinus2 != null && vAny.ClosePriceTMinus2 > 0) {
          const existingT2 = (existing as any).ClosePriceTMinus2;
          if (existingT2 == null || existingT2 === 0) {
            (existing as any).ClosePriceTMinus2 = vAny.ClosePriceTMinus2;
          }
        }

        const ask = numOrZero(v.AskPrice, existing.AskPrice);
        const bid = numOrZero(v.BidPrice, existing.BidPrice);
        const BidSize = numOrZero(v.BidSize, (existing as any).BidSize);
        const AskSize = numOrZero(v.AskSize, (existing as any).AskSize);
        const LastOrClosePrice = getLastOrClosePrice({
          LastTradePrice: v.LastTradePrice,
          ClosePrice: (existing as any).ClosePrice,
          IsSettlementSet: (existing as any).IsSettlementSet,
        });
        const BestPriceCalculated = getBestPrice({ bid, ask, last: LastOrClosePrice, defaultValue: 'avgBidAsk' });
        const ReceivedTime = v.QuoteTime;
        if (ReceivedTime) {
          const ts = getLocalDateFromTimezone(new Date(ReceivedTime)).valueOf();
          if (!latestReceivedTime || ts > latestReceivedTime) latestReceivedTime = ts;
        }
        let changeDirection: SymbolChangeDirection = undefined;
        if (existingValue && existingValue.LastOrClosePrice > LastOrClosePrice) changeDirection = 'down';
        else if (existingValue && existingValue.LastOrClosePrice < LastOrClosePrice) changeDirection = 'up';

        // Volume delta: cumulative `v` from feed vs last cumulative in meta. Several pitfalls:
        // - JSON quotes often omit `v`; older parser used 0 and wiped baseline → next quote exploded volume.
        // - REST can leave Volume stale; meta.lastFeedCumulativeVolume tracks the feed truth.
        // - Settlement / partial messages may not carry Volume — never overwrite cumulative with undefined/NaN.
        const previousMetaFeed = existing.meta ? { ...existing.meta } : {};
        const metaLast = previousMetaFeed.lastFeedCumulativeVolume;
        const existingVol = (existing as any).Volume as number | undefined;

        const incomingVol = v.Volume;
        const hasFiniteIncoming = incomingVol != null && Number.isFinite(incomingVol);

        const establishedCumulative =
          (metaLast != null && Number.isFinite(metaLast) && metaLast > 0) ||
          (existingVol != null && Number.isFinite(existingVol) && existingVol > 0);

        const treatVolumeAbsent =
          !hasFiniteIncoming ||
          (incomingVol === 0 && establishedCumulative) ||
          (typeof incomingVol === 'number' && incomingVol < 0);

        const preservedCumulative =
          metaLast != null && Number.isFinite(metaLast)
            ? metaLast
            : existingVol != null && Number.isFinite(existingVol)
              ? existingVol
              : undefined;

        const newCumulativeForDelta = treatVolumeAbsent ? preservedCumulative : incomingVol;

        const baseline =
          metaLast != null && Number.isFinite(metaLast)
            ? metaLast
            : existingVol != null && Number.isFinite(existingVol)
              ? existingVol
              : undefined;

        const hasValidBaseline =
          baseline != null &&
          newCumulativeForDelta != null &&
          Number.isFinite(baseline) &&
          Number.isFinite(newCumulativeForDelta);

        const rawDelta = hasValidBaseline ? newCumulativeForDelta - baseline : 0;
        const LastTradeVolume = Number.isFinite(rawDelta) && rawDelta >= 0 ? rawDelta : 0;

        const nextLastFeedCumulative = treatVolumeAbsent ? preservedCumulative : incomingVol;
        const nextVolumeDisplay = treatVolumeAbsent
          ? existingVol != null && Number.isFinite(existingVol)
            ? existingVol
            : preservedCumulative
          : incomingVol;
        const volumeOut =
          nextVolumeDisplay != null && Number.isFinite(nextVolumeDisplay)
            ? nextVolumeDisplay
            : existingVol != null && Number.isFinite(existingVol)
              ? existingVol
              : 0;

        nextData[v.Symbol] = {
          ...(existing as any),
          LastTradePrice: v.LastTradePrice,
          NetChange: v.NetChange,
          High: v.High,
          Low: v.Low,
          Open: isPremarket() ? 0 : numOrZero((existing as any).Open),
          Volume: volumeOut,
          ChangePercentage: v.ChangePercentage,
          AskPrice: ask,
          BidPrice: bid,
          isFetching: false,
          isLoaded: true,
          BestPriceCalculated,
          LastOrClosePrice,
          ReceivedTime,
          changeDirection,
          BidSize,
          AskSize,
          LastTradeVolume,
          meta: {
            ...previousMetaFeed,
            ...(nextLastFeedCumulative != null && Number.isFinite(nextLastFeedCumulative)
              ? { lastFeedCumulativeVolume: nextLastFeedCumulative }
              : {}),
          },
        } as any;
      }

      return {
        ...state,
        quoteMetadata: {
          ...state.quoteMetadata,
          isFetching: false,
          data: nextData,
          latestReceivedTime,
        },
      };
    }

    default:
      return state;
  }
};
